'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function GolfHandicapCalculator() {
  const t = useTranslations("tools.golfHandicapCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState<'course' | 'index'>('course');

  // Course Handicap State
  const [courseInputs, setCourseInputs] = useState({
    handicapIndex: 10,
    courseRating: 70,
    slopeRating: 121,
    coursePar: 72
  });
  const [courseResult, setCourseResult] = useState<number | null>(null);

  // Handicap Index State
  const [rounds, setRounds] = useState([
    { id: 1, courseRating: 70.3, slopeRating: 126, score18: 85, score9: 0, pcc: 0 },
    { id: 2, courseRating: 35.3, slopeRating: 122, score18: 0, score9: 41, pcc: 0 },
    { id: 3, courseRating: 66.8, slopeRating: 120, score18: 82, score9: 0, pcc: 0 }
  ]);
  const [nextRoundId, setNextRoundId] = useState(4);
  const [indexResult, setIndexResult] = useState<{
    handicapIndex: number;
    differentials: Array<{
      round: number;
      differential: number;
      score: string;
      courseRating: number;
      slopeRating: number;
      isUsed: boolean;
    }>;
  } | null>(null);
  const [indexError, setIndexError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const handleCourseCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const { handicapIndex, courseRating, slopeRating, coursePar } = courseInputs;
    const courseHandicap = Math.round((handicapIndex * slopeRating / 113) + (courseRating - coursePar));
    setCourseResult(courseHandicap);
  };

  const handleClearCourse = () => {
    setCourseInputs({
      handicapIndex: 10,
      courseRating: 70,
      slopeRating: 121,
      coursePar: 72
    });
    setCourseResult(null);
  };

  const handleIndexCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setIndexError('');

    const differentials: Array<{
      round: number;
      differential: number;
      score: string;
      courseRating: number;
      slopeRating: number;
    }> = [];

    rounds.forEach(round => {
      if (!round.courseRating || !round.slopeRating) return;

      let adjustedScore: number | null = null;
      let scoreDisplay = '';

      if (round.score18) {
        adjustedScore = parseFloat(round.score18.toString());
        scoreDisplay = round.score18.toString();
      } else if (round.score9) {
        adjustedScore = parseFloat(round.score9.toString()) * 2 - round.courseRating;
        scoreDisplay = `${round.score9} (9-hole)`;
      }

      if (adjustedScore === null) return;

      const differential = ((adjustedScore - round.courseRating - round.pcc) * 113 / round.slopeRating);
      differentials.push({
        round: round.id,
        differential,
        score: scoreDisplay,
        courseRating: round.courseRating,
        slopeRating: round.slopeRating
      });
    });

    if (differentials.length < 3) {
      setIndexError(t('errorMinRounds'));
      setIndexResult(null);
      return;
    }

    differentials.sort((a, b) => a.differential - b.differential);

    let numToUse: number;
    if (differentials.length <= 6) numToUse = 1;
    else if (differentials.length <= 8) numToUse = 2;
    else if (differentials.length <= 10) numToUse = 3;
    else if (differentials.length <= 12) numToUse = 4;
    else if (differentials.length <= 14) numToUse = 5;
    else if (differentials.length <= 16) numToUse = 6;
    else if (differentials.length <= 18) numToUse = 7;
    else numToUse = 8;

    const avgDifferential = differentials.slice(0, numToUse).reduce((sum, d) => sum + d.differential, 0) / numToUse;
    const handicapIndex = Math.floor(avgDifferential * 0.96 * 10) / 10;

    setIndexResult({
      handicapIndex,
      differentials: differentials.map((d, idx) => ({
        ...d,
        isUsed: idx < numToUse
      }))
    });
  };

  const handleClearIndex = () => {
    setRounds([
      { id: 1, courseRating: 0, slopeRating: 0, score18: 0, score9: 0, pcc: 0 },
      { id: 2, courseRating: 0, slopeRating: 0, score18: 0, score9: 0, pcc: 0 },
      { id: 3, courseRating: 0, slopeRating: 0, score18: 0, score9: 0, pcc: 0 }
    ]);
    setIndexResult(null);
    setIndexError('');
  };

  const handleAddRound = () => {
    setRounds([...rounds, {
      id: nextRoundId,
      courseRating: 0,
      slopeRating: 0,
      score18: 0,
      score9: 0,
      pcc: 0
    }]);
    setNextRoundId(nextRoundId + 1);
  };

  const handleRemoveRound = (id: number) => {
    setRounds(rounds.filter(r => r.id !== id));
  };

  const updateRound = (id: number, field: string, value: any) => {
    setRounds(rounds.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const colors = {
    primary: '#16a34a',
    primaryHover: '#15803d',
    secondary: '#22c55e',
    accent: '#86efac',
    textPrimary: '#166534',
    textSecondary: '#4b5563',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(34, 197, 94, 0.2)',
    gradientStart: '#f0fdf4',
    gradientEnd: '#dcfce7',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '800px' },
    { maxWidth: '1000px' }
  );

  const tabsContainerStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginBottom: '2rem', justifyContent: 'center', flexWrap: 'wrap' as const },
    {},
    {},
    {}
  );

  const tabButtonStyle = (isActive: boolean) => getResponsiveStyle(
    {
      background: isActive ? colors.secondary : 'white',
      color: isActive ? 'white' : colors.textPrimary,
      border: `2px solid ${colors.secondary}`,
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
    },
    { padding: '0.75rem 1.5rem', fontSize: '0.875rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const sectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const sectionTitleStyle = getResponsiveStyle(
    { color: colors.textPrimary, textAlign: 'center' as const, marginBottom: '1rem', fontWeight: 600 },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const sectionDescStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '1.5rem', textAlign: 'center' as const, lineHeight: 1.6 },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', color: colors.textPrimary, fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `2px solid ${colors.glassBorder}`,
      borderRadius: '0.5rem',
      transition: 'border-color 0.3s',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' as const },
    {},
    {},
    {}
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s'
    },
    { padding: '0.875rem 2rem', fontSize: '1rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      background: '#f3f4f6',
      color: colors.textSecondary,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s'
    },
    { padding: '0.875rem 2rem', fontSize: '1rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: `linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)`,
      borderRadius: '1rem',
      border: `2px solid ${colors.secondary}`
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '1rem',
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
      alignItems: 'center'
    },
    { flexDirection: 'column' as const, gap: '0.5rem' },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const resultLabelStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 600 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: colors.secondary, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const tableContainerStyle = getResponsiveStyle(
    { width: '100%', overflowX: 'auto' as const, marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const tableStyle = getResponsiveStyle(
    { width: '100%', borderCollapse: 'collapse' as const },
    { minWidth: '600px' },
    { minWidth: '600px' },
    {}
  );

  const thStyle = getResponsiveStyle(
    {
      background: colors.secondary,
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '0.75rem 0.5rem', fontSize: '0.75rem' },
    { padding: '0.75rem 0.5rem', fontSize: '0.875rem' },
    { padding: '0.75rem 0.5rem', fontSize: '0.875rem' }
  );

  const tdStyle = getResponsiveStyle(
    { padding: '0.5rem', textAlign: 'center' as const },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const tableInputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '0.25rem',
      textAlign: 'center' as const,
      boxSizing: 'border-box' as const
    },
    { padding: '0.5rem', fontSize: '0.875rem' },
    { padding: '0.5rem', fontSize: '1rem' },
    { padding: '0.5rem', fontSize: '1rem' }
  );

  const removeButtonStyle = {
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '0.25rem',
    padding: '0.25rem 0.5rem',
    cursor: 'pointer',
    fontSize: '0.875rem'
  };

  const errorStyle = getResponsiveStyle(
    {
      background: '#fee2e2',
      color: '#991b1b',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      textAlign: 'center' as const
    },
    { padding: '1rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(22, 163, 74, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: `1px solid ${colors.glassBorder}`
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-all' as const,
    fontSize: '0.875rem'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>⛳ {t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={tabsContainerStyle}>
            <button
              style={tabButtonStyle(activeTab === 'course')}
              onClick={() => setActiveTab('course')}
            >
              {t('tabCourseHandicap')}
            </button>
            <button
              style={tabButtonStyle(activeTab === 'index')}
              onClick={() => setActiveTab('index')}
            >
              {t('tabHandicapIndex')}
            </button>
          </div>

          {/* Course Handicap Calculator */}
          {activeTab === 'course' && (
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>{t('courseTitle')}</h3>
              <p style={sectionDescStyle}>{t('courseDescription')}</p>

              <form onSubmit={handleCourseCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="handicap-index">{t('labelHandicapIndex')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="handicap-index"
                    step="0.1"
                    value={courseInputs.handicapIndex}
                    onChange={(e) => setCourseInputs({...courseInputs, handicapIndex: parseFloat(e.target.value) || 0})}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="course-rating">{t('labelCourseRating')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="course-rating"
                    step="0.1"
                    value={courseInputs.courseRating}
                    onChange={(e) => setCourseInputs({...courseInputs, courseRating: parseFloat(e.target.value) || 0})}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="slope-rating">{t('labelSlopeRating')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="slope-rating"
                    value={courseInputs.slopeRating}
                    onChange={(e) => setCourseInputs({...courseInputs, slopeRating: parseFloat(e.target.value) || 0})}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="course-par">{t('labelCoursePar')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="course-par"
                    value={courseInputs.coursePar}
                    onChange={(e) => setCourseInputs({...courseInputs, coursePar: parseFloat(e.target.value) || 0})}
                  />
                </div>

                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateButtonStyle}>{t('buttonCalculate')}</button>
                  <button type="button" style={clearButtonStyle} onClick={handleClearCourse}>{t('buttonClear')}</button>
                </div>
              </form>

              {courseResult !== null && (
                <div style={resultsStyle}>
                  <h3 style={sectionTitleStyle}>{t('courseResultTitle')}</h3>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('resultCourseHandicap')}</span>
                    <span style={resultValueStyle}>{courseResult}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Handicap Index Calculator */}
          {activeTab === 'index' && (
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>{t('indexTitle')}</h3>
              <p style={sectionDescStyle}>{t('indexDescription')}</p>

              <form onSubmit={handleIndexCalculate}>
                <div style={tableContainerStyle}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t('tableRound')}</th>
                        <th style={thStyle}>{t('tableCourseRating')}</th>
                        <th style={thStyle}>{t('tableSlopeRating')}</th>
                        <th style={thStyle}>{t('table18HoleScore')}</th>
                        <th style={thStyle}>{t('table9HoleScore')}</th>
                        <th style={thStyle}>{t('tablePCC')}</th>
                        <th style={thStyle}>{t('tableAction')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rounds.map((round, idx) => (
                        <tr key={round.id} style={idx % 2 === 1 ? { background: colors.gradientStart } : {}}>
                          <td style={tdStyle}>{idx + 1}</td>
                          <td style={tdStyle}>
                            <input
                              style={tableInputStyle}
                              type="number"
                              step="0.1"
                              value={round.courseRating || ''}
                              onChange={(e) => updateRound(round.id, 'courseRating', parseFloat(e.target.value) || 0)}
                            />
                          </td>
                          <td style={tdStyle}>
                            <input
                              style={tableInputStyle}
                              type="number"
                              value={round.slopeRating || ''}
                              onChange={(e) => updateRound(round.id, 'slopeRating', parseFloat(e.target.value) || 0)}
                            />
                          </td>
                          <td style={tdStyle}>
                            <input
                              style={tableInputStyle}
                              type="number"
                              value={round.score18}
                              onChange={(e) => updateRound(round.id, 'score18', e.target.value)}
                            />
                          </td>
                          <td style={tdStyle}>
                            <input
                              style={tableInputStyle}
                              type="number"
                              value={round.score9}
                              onChange={(e) => updateRound(round.id, 'score9', e.target.value)}
                            />
                          </td>
                          <td style={tdStyle}>
                            <input
                              style={tableInputStyle}
                              type="number"
                              step="0.1"
                              value={round.pcc}
                              onChange={(e) => updateRound(round.id, 'pcc', parseFloat(e.target.value) || 0)}
                            />
                          </td>
                          <td style={tdStyle}>
                            <button
                              type="button"
                              style={removeButtonStyle}
                              onClick={() => handleRemoveRound(round.id)}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateButtonStyle}>{t('buttonCalculateIndex')}</button>
                  <button type="button" style={clearButtonStyle} onClick={handleClearIndex}>{t('buttonClearAll')}</button>
                  <button type="button" style={clearButtonStyle} onClick={handleAddRound}>{t('buttonAddRound')}</button>
                </div>
              </form>

              {indexError && (
                <div style={errorStyle}>{indexError}</div>
              )}

              {indexResult && (
                <div style={resultsStyle}>
                  <h3 style={sectionTitleStyle}>{t('indexResultTitle')}</h3>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('resultHandicapIndex')}</span>
                    <span style={resultValueStyle}>{indexResult.handicapIndex.toFixed(1)}</span>
                  </div>

                  <div style={tableContainerStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>{t('tableRound')}</th>
                          <th style={thStyle}>{t('tableScore')}</th>
                          <th style={thStyle}>{t('tableCourseRating')}</th>
                          <th style={thStyle}>{t('tableSlope')}</th>
                          <th style={thStyle}>{t('tableDifferential')}</th>
                          <th style={thStyle}>{t('tableUsed')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {indexResult.differentials.map((d, idx) => (
                          <tr
                            key={idx}
                            style={d.isUsed ? { background: '#d1fae5', fontWeight: 600 } : idx % 2 === 1 ? { background: colors.gradientStart } : {}}
                          >
                            <td style={tdStyle}>{d.round}</td>
                            <td style={tdStyle}>{d.score}</td>
                            <td style={tdStyle}>{d.courseRating}</td>
                            <td style={tdStyle}>{d.slopeRating}</td>
                            <td style={tdStyle}>{d.differential.toFixed(1)}</td>
                            <td style={tdStyle}>{d.isUsed ? '✓' : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li>{t('howToUseStep1')}</li>
          <li>{t('howToUseStep2')}</li>
          <li>{t('howToUseStep3')}</li>
          <li>{t('howToUseStep4')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li>{t('insight1')}</li>
          <li>{t('insight2')}</li>
          <li>{t('insight3')}</li>
          <li>{t('insight4')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={{...faqItemStyle, borderBottom: 'none'}}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li>
            <a href="https://stix.golf/blogs/rough-thoughts/golf-handicap-explained-a-beginner-s-guide" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li>
            <a href="https://en.wikipedia.org/wiki/Handicap_(golf)" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li>
            <a href="https://austinsga.org/Golf%20handicap%20formula%20USGA%20handicap%20formula%20in%20plain%20English.htm" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li>
            <a href="https://ncga.org/handicap-101" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li>
            <a href="https://www.usga.org/handicapping/roh/Content/rules/1%201%20Purpose%20of%20the%20World%20Handicap%20System.htm" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}